import { ALCOVE, BOOK_STAND, ROOM } from "./layout";
import { SECTION_IDS, sectionOf } from "./sections";

const { W, D } = ROOM;
const M = 0.55;
export const ARCH = { w: 3.4, h: 3.3 };
export const LOVE_C = { x: -W / 2 - 6, z: 0, r: 6 };
export const NATURE = { x0: W / 2, x1: W / 2 + 24, hz: 4 };
export const SPIRIT = { hx: 6, z0: -D / 2 - 12, z1: -D / 2 };
export const FIRE = { x0: -10.5, x1: -3.5, z0: D / 2, z1: D / 2 + 7 };
export const CORR = { z0: D / 2, z1: D / 2 + 29, hw: 1.7, x: 7 };
export const corrX = (z) => CORR.x + 2.0 * Math.sin((z - CORR.z0) / 5);
const corrDX = (z) => 0.4 * Math.cos((z - CORR.z0) / 5);
export const FEEL = { z: -D / 2, w: 2.3, h: 3.0, xs: { fire: -8, spirit: -4, nature: 0, thoughts: 4, love: 8 } };
export const FEEL_STAND = { x: 0, z: 3.0, yaw: 0 };
export const MEMORY_STAND = { x: 0.6, z: 4.4, yaw: Math.PI };
export const MEMORY_SIGN = { x: 9.1, z: -5.3, rot: Math.atan2(-9.1, 8.3) };
export const feelFront = (id) => ({ x: FEEL.xs[id], z: FEEL.z + 1.6 });
// Three Living Windows on the wall opposite the Feeling Wall, right of the Memory Wall alcove.
export const WINDOWS = {
  z: D / 2 - 0.03, w: 1.5, h: 2.9, y0: 0.72, cx: 6.8,
  list: [
    { id: "lotus", slug: "the-lotus-pond", x: 4.6 },
    { id: "love", slug: "love-must-feel-like-this", x: 6.8 },
    { id: "woods", slug: "the-dream", x: 9.0 },
  ],
};
export const BENCH_W = { x: WINDOWS.cx, z: D / 2 - 1.5 };
// far enough back that all three windows fit the view (phones stand further away)
export function windowsStand(x = WINDOWS.cx) {
  const half = Math.tan((33 * Math.PI) / 180) * (window.innerWidth / Math.max(1, window.innerHeight));
  const span = x === WINDOWS.cx ? 3.05 : WINDOWS.w / 2 + 0.5;
  const dist = Math.min(10.2, Math.max(5.6, span / half));
  return { x, z: WINDOWS.z - dist, yaw: Math.PI };
}
export const ROOM_H = { love: 5.4, nature: 4.6, spirit: 5.2, thoughts: 3.6, fire: 3.8 };

// n = normal pointing into the hall
export const ARCHES = {
  love: { x: -W / 2, z: 0, nx: 1, nz: 0 },
  nature: { x: W / 2, z: 0, nx: -1, nz: 0 },
  spirit: { x: 0, z: -D / 2, nx: 0, nz: 1 },
  thoughts: { x: 7, z: D / 2, nx: 0, nz: -1 },
  fire: { x: -7, z: D / 2, nx: 0, nz: -1 },
};

const HERO = { love: "love-must-feel-like-this", nature: "the-lotus-pond", spirit: "mor-pankh", thoughts: "the-storm-of-whispering-thoughts", fire: "simmering-passion" };
const HERO_AT = {
  love: { x: -W / 2, z: -3.6, nx: 1, nz: 0 },
  nature: { x: W / 2, z: -3.6, nx: -1, nz: 0 },
  spirit: { x: 3.7, z: -D / 2, nx: 0, nz: 1 },
  thoughts: { x: 9.75, z: D / 2, nx: 0, nz: -1 },
  fire: { x: -9.75, z: D / 2, nx: 0, nz: -1 },
};

const inRect = (x, z, x0, x1, z0, z1) => x >= x0 && x <= x1 && z >= z0 && z <= z1;

export function regionAt(x, z) {
  if (x < -W / 2 && Math.hypot(x - LOVE_C.x, z - LOVE_C.z) < LOVE_C.r + 0.2) return "love";
  if (x > W / 2 && Math.abs(z) < NATURE.hz + 0.2) return "nature";
  if (z < -D / 2 && Math.abs(x) < SPIRIT.hx + 0.2) return "spirit";
  if (z > D / 2 && x > 3) return "thoughts";
  if (z > D / 2 && x < -3) return "fire";
  return "hall";
}

export function walkable(x, z) {
  if (Math.abs(x) <= W / 2 - M && Math.abs(z) <= D / 2 - M) return true;
  if (Math.abs(x) <= ALCOVE.w / 2 - M && z > 0 && z <= D / 2 + ALCOVE.d - M) return true;
  if (Math.hypot(x - LOVE_C.x, z - LOVE_C.z) <= LOVE_C.r - M) return true;
  if (inRect(x, z, NATURE.x0 + M, NATURE.x1 - M, -NATURE.hz + M, NATURE.hz - M)) return true;
  if (inRect(x, z, -SPIRIT.hx + M, SPIRIT.hx - M, SPIRIT.z0 + M, SPIRIT.z1 - M)) return true;
  if (inRect(x, z, FIRE.x0 + M, FIRE.x1 - M, FIRE.z0 + M, FIRE.z1 - M)) return true;
  return z >= CORR.z0 + M && z <= CORR.z1 - M && Math.abs(x - corrX(z)) <= CORR.hw - M;
}

const S = (x, z, nx, nz, section, off = 0.06) => ({ x: x + nx * off, z: z + nz * off, nx, nz, rotY: Math.atan2(nx, nz), section });

function roomSlots(zone, n) {
  const out = [];
  if (zone === "love") {
    const a0 = Math.PI / 2 + 0.95;
    const a1 = Math.PI / 2 + 2 * Math.PI - 0.95;
    for (let i = 0; i < n; i++) {
      const a = n === 1 ? Math.PI * 1.5 : a0 + ((a1 - a0) * i) / (n - 1);
      const r = LOVE_C.r - 0.1;
      out.push(S(LOVE_C.x + Math.sin(a) * r, LOVE_C.z + Math.cos(a) * r, -Math.sin(a), -Math.cos(a), zone, 0));
    }
  } else if (zone === "nature") {
    const step = n > 1 ? Math.min(2.5, 17 / (n - 1)) : 0;
    for (let i = 0; i < n; i++) {
      const x = NATURE.x0 + 3.5 + i * step;
      out.push(i % 2 ? S(x, NATURE.hz, 0, -1, zone) : S(x, -NATURE.hz, 0, 1, zone));
    }
  } else if (zone === "spirit") {
    const z = SPIRIT.z0;
    const spots = [[-2.6, z, 0, 1], [2.6, z, 0, 1], [-6, z + 3.8, 1, 0], [6, z + 3.8, -1, 0], [-6, z + 7.8, 1, 0], [6, z + 7.8, -1, 0], [-6, z + 9.6, 1, 0], [6, z + 9.6, -1, 0]];
    for (let i = 0; i < n; i++) {
      const [x, zz, nx, nz] = spots[i % spots.length];
      out.push(S(x, zz, nx, nz, zone));
    }
  } else if (zone === "thoughts") {
    const step = n > 1 ? Math.min(3.8, 24 / (n - 1)) : 0;
    for (let i = 0; i < n; i++) {
      const z = CORR.z0 + 3.2 + i * step;
      const side = i % 2 ? 1 : -1;
      const k = Math.hypot(1, corrDX(z));
      out.push(S(corrX(z) + side * CORR.hw, z, -side / k, (side * corrDX(z)) / k, zone));
    }
  } else {
    const mz = (FIRE.z0 + FIRE.z1) / 2;
    const spots = [[FIRE.x0, mz, 1, 0], [FIRE.x1, mz, -1, 0], [-7, FIRE.z1, 0, -1], [FIRE.x0, mz + 0.9, 1, 0], [FIRE.x1, mz + 0.9, -1, 0]];
    for (let i = 0; i < n; i++) {
      const [x, z, nx, nz] = spots[i % spots.length];
      out.push(S(x, z, nx, nz, zone));
    }
  }
  return out;
}

export function worldLayout(paintings) {
  const groups = Object.fromEntries(SECTION_IDS.map((id) => [id, []]));
  paintings.forEach((p) => groups[sectionOf(p)].push(p));
  const slots = {};
  const heroes = [];
  SECTION_IDS.forEach((id) => {
    const list = groups[id];
    roomSlots(id, list.length).forEach((s, i) => (slots[list[i].id] = s));
    const hero = list.find((p) => p.slug === HERO[id]) || list[0];
    const h = HERO_AT[id];
    if (hero) heroes.push({ painting: hero, slot: S(h.x, h.z, h.nx, h.nz, id) });
  });
  return { slots, groups, heroes };
}

export function minSpacing(slots) {
  const list = Object.values(slots);
  let m = Infinity;
  list.forEach((a, i) => list.slice(i + 1).forEach((b) => (m = Math.min(m, Math.hypot(a.x - b.x, a.z - b.z)))));
  return m;
}

export function frontOf(s, want = 3) {
  for (let k = want; k > 0.8; k -= 0.15) {
    const x = s.x + s.nx * k;
    const z = s.z + s.nz * k;
    if (walkable(x, z)) return { x, z, yaw: Math.atan2(s.nx, s.nz) };
  }
  return { x: s.x + s.nx, z: s.z + s.nz, yaw: Math.atan2(s.nx, s.nz) };
}

export function archFront(id, k = 1.6) {
  const a = ARCHES[id];
  return { x: a.x + a.nx * k, z: a.z + a.nz * k };
}

// in-hall glides only; rooms are reached through the Feeling Wall portals
export function glidePath(from, target) {
  const d = target === "memory" ? MEMORY_STAND : target === "windows" ? windowsStand() : target === "flipbook" ? BOOK_STAND : FEEL_STAND;
  return { pts: [{ x: from.x, z: from.z }, { x: d.x, z: d.z }], yaw: d.yaw };
}

export function roomEntry(id) {
  const a = ARCHES[id];
  if (id === "thoughts") {
    const z = CORR.z0 + 1.8;
    return { x: corrX(z), z, yaw: Math.atan2(-corrDX(z) * 2, -1) };
  }
  return { x: a.x - a.nx * 2.6, z: a.z - a.nz * 2.6, yaw: Math.atan2(a.nx, a.nz) };
}
